// A soft two-tone chime for the resident's display, synthesized with the Web
// Audio API so there's no audio file to bundle or fetch (and nothing to fail to
// load on a locked-down kiosk). Used when a new message appears, if the admin has
// enabled the per-resident "New-message chime" setting.
//
// The AudioContext is created lazily and reused. On a kiosk WebView autoplay is
// allowed; in a normal browser the first tone may be blocked until the page has
// had a user gesture — we resume() best-effort and swallow failures so a blocked
// chime never disrupts the board.

let ctx;

export function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!ctx) ctx = new AC();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const t0 = ctx.currentTime;
    // A gentle "ding-dong": E5 then C5, each a soft sine that fades out.
    const notes = [
      { freq: 659.25, at: 0 },
      { freq: 523.25, at: 0.18 },
    ];
    notes.forEach(({ freq, at }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = t0 + at;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);   // quick attack
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.9); // soft decay
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.95);
    });
  } catch (_) {
    /* audio unavailable (no Web Audio, blocked, etc.) — ignore */
  }
}

// A more insistent chime for an alarm going off: three rising tones, a bit
// louder/longer than the new-message chime so it draws attention.
export function playAlarm() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!ctx) ctx = new AC();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const t0 = ctx.currentTime;
    const notes = [
      { freq: 587.33, at: 0 },     // D5
      { freq: 739.99, at: 0.22 },  // F#5
      { freq: 987.77, at: 0.44 },  // B5
    ];
    notes.forEach(({ freq, at }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      const start = t0 + at;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(0.32, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.55);
    });
  } catch (_) {
    /* audio unavailable — ignore */
  }
}
